import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
    LayoutDashboard,
    Package,
    FileText,
    ShieldCheck,
    LogOut,
    Stethoscope,
    Eye,
    Rocket,
    User,
    Image as ImageIcon,
    QrCode,
    Search as SearchIcon,
    BarChart3,
    Globe,
    CreditCard,
    LifeBuoy,
    Settings as SettingsIcon,
    Lock,
    Sparkles,
    BookOpen,
    Share2,
    Award,
    MessageCircle,
    Menu,
    X,
    ChevronDown,
    Calendar as CalendarIcon,
} from "lucide-react";
import { getUser, clearSession } from "@/lib/api";
import api from "@/lib/api";
import { PROFILE_STATUS_STYLES } from "@/lib/constants";
import NotificationsBell from "@/components/NotificationsBell";

// Nav layout: (label, path, icon, feature_code | null, testid)
// feature_code = null → always available
// If feature_code set: entitlement decides — unlocked → real page, else LockedFeature.
const NAV_GROUPS = [
    {
        title: "Workspace",
        items: [
            { label: "Dashboard", to: "/doctor", icon: LayoutDashboard, feature: null, testid: "sidebar-dashboard" },
            { label: "My Digital Identity", to: "/doctor/publish", icon: Rocket, feature: "A01", testid: "sidebar-publish" },
            { label: "Profile", to: "/doctor/onboarding", icon: User, feature: null, testid: "sidebar-onboarding" },
            { label: "Credentials", to: "/doctor/credentials", icon: Award, feature: null, testid: "sidebar-credentials" },
            { label: "Final Review", to: "/doctor/review", icon: Eye, feature: null, testid: "sidebar-review" },
            { label: "AI Studio", to: "/doctor/ai-studio", icon: Sparkles, feature: "H01", testid: "sidebar-ai-studio" },
        ],
    },
    {
        title: "Presence",
        items: [
            { label: "Analytics", to: "/doctor/analytics", icon: BarChart3, feature: "A01", testid: "sidebar-analytics" },
            { label: "Leads", to: "/doctor/leads", icon: LifeBuoy, feature: "J01", testid: "sidebar-leads" },
            { label: "Appointments", to: "/doctor/appointments", icon: CalendarIcon, feature: null, testid: "sidebar-appointments" },
            { label: "Second Opinions", to: "/doctor/second-opinions", icon: Stethoscope, feature: null, testid: "sidebar-second-opinions" },
            { label: "Content & Trust", to: "/doctor/content-trust", icon: Sparkles, feature: null, testid: "sidebar-content-trust" },
            { label: "Smart FAQ queue", to: "/doctor/smart-faq", icon: Sparkles, feature: null, testid: "sidebar-smart-faq" },
            { label: "Media Gallery", to: "/doctor/gallery", icon: ImageIcon, feature: "I03", testid: "sidebar-gallery" },
            { label: "QR & Card", to: "/doctor/qr-card", icon: QrCode, feature: "A04", testid: "sidebar-qrcard" },
            { label: "SEO", to: "/doctor/seo", icon: SearchIcon, feature: "H03", testid: "sidebar-seo" },
            { label: "Articles", to: "/doctor/articles", icon: BookOpen, feature: "D01", testid: "sidebar-articles" },
            { label: "Content Hub", to: "/doctor/content", icon: FileText, feature: "D01", testid: "sidebar-content-hub" },
            { label: "Identity Timeline", to: "/doctor/identity", icon: FileText, testid: "sidebar-identity" },
            { label: "AI Language Studio", to: "/doctor/ai-language", icon: Sparkles, feature: "H01", testid: "sidebar-ai-language" },
            { label: "Translations (EN/HI/MR)", to: "/doctor/translations", icon: Sparkles, feature: "A10", testid: "sidebar-translations" },
            { label: "Public Feedback", to: "/doctor/feedback", icon: MessageCircle, feature: null, testid: "sidebar-feedback" },
            { label: "Public FAQs", to: "/doctor/faqs", icon: MessageCircle, feature: null, testid: "sidebar-faqs" },
            { label: "Social & Local", to: "/doctor/social", icon: Share2, feature: "E01", testid: "sidebar-social" },
            { label: "Documents", to: "/doctor/documents", icon: FileText, feature: "L01", testid: "sidebar-documents" },
            { label: "Domain", to: "/doctor/domain", icon: Globe, feature: "A08", testid: "sidebar-domain" },
        ],
    },
    {
        title: "Account",
        items: [
            { label: "Packages", to: "/doctor/packages", icon: Package, feature: null, testid: "sidebar-packages" },
            { label: "Billing & AMC", to: "/doctor/billing", icon: CreditCard, feature: null, testid: "sidebar-billing" },
            { label: "Features", to: "/doctor/features", icon: ShieldCheck, feature: null, testid: "sidebar-features" },
            { label: "Support", to: "/doctor/support", icon: LifeBuoy, feature: "O01", testid: "sidebar-support" },
            { label: "Settings", to: "/doctor/settings", icon: SettingsIcon, feature: null, testid: "sidebar-settings" },
        ],
    },
];

const BUILT_ROUTES = new Set([
    "/doctor",
    "/doctor/onboarding",
    "/doctor/credentials",
    "/doctor/review",
    "/doctor/publish",
    "/doctor/packages",
    "/doctor/invoices",
    "/doctor/features",
    "/doctor/ai-studio",
    "/doctor/analytics",
    "/doctor/documents",
    "/doctor/leads",
    "/doctor/appointments",
    "/doctor/second-opinions",
    "/doctor/content-trust",
    "/doctor/smart-faq",
    "/doctor/billing",
    "/doctor/domain",
    "/doctor/settings",
    "/doctor/gallery",
    "/doctor/qr-card",
    "/doctor/seo",
    "/doctor/articles",
    "/doctor/social",
    "/doctor/support",
]);

export default function DoctorShell({ children, doctor }) {
    const user = getUser();
    const navigate = useNavigate();
    const status = doctor?.profile_status || "draft";
    const badge = PROFILE_STATUS_STYLES[status] || PROFILE_STATUS_STYLES.draft;

    const [entitlements, setEntitlements] = useState(null);
    const [pkg, setPkg] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    useEffect(() => {
        api.get("/me/entitlements").then((r) => setEntitlements(r.data)).catch(() => setEntitlements(null));
    }, []);
    useEffect(() => {
        if (doctor?.package_code) {
            api.get("/packages").then((r) => {
                const p = (r.data || []).find((x) => x.code === doctor.package_code);
                setPkg(p);
            }).catch(() => {});
        }
    }, [doctor?.package_code]);

    const unlocked = new Set((entitlements?.items || []).filter((i) => i.unlocked).map((i) => i.feature_code));

    function resolveLink(item) {
        if (!item.feature) return item.to;
        if (unlocked.has(item.feature)) {
            return BUILT_ROUTES.has(item.to) ? item.to : `/doctor/coming-soon?code=${item.feature}`;
        }
        return `/doctor/locked?code=${item.feature}`;
    }

    const initials = (user?.full_name || "D")
        .replace(/^Dr\.?\s*/i, "")
        .split(" ")
        .filter(Boolean)
        .map((s) => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();

    return (
        <div className="min-h-screen bg-[#F5F7FB] flex" data-testid="doctor-shell">
            {/* Mobile backdrop */}
            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" />
            )}
            {/* Sidebar — off-canvas on mobile, static on lg+ */}
            <aside className={"fixed lg:static inset-y-0 left-0 w-72 shrink-0 bg-[#0F1B2D] text-white flex flex-col z-50 transform transition-transform lg:translate-x-0 " + (sidebarOpen ? "translate-x-0" : "-translate-x-full")}>
                <div className="px-5 pt-6 pb-5 flex items-center gap-2.5">
                    <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-10 w-10 rounded-lg object-contain bg-white p-0.5 shadow-lg shadow-[#2563EB]/30" data-testid="doctor-shell-brand-icon" />
                    <div className="leading-tight flex-1 min-w-0">
                        <div className="text-[15px] font-bold tracking-tight">DOCTOR DIGITAL ID</div>
                        <div className="text-[10px] uppercase tracking-widest text-white/45">Your Digital Identity Partner</div>
                    </div>
                    <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white/70 hover:text-white p-1" data-testid="sidebar-close" aria-label="Close menu">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 pb-4">
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-white/45 mb-1.5">Profile status</div>
                        <span className={"ddi-badge " + badge.cls} data-testid="sidebar-status-badge">{badge.label}</span>
                        {pkg && (
                            <div className="mt-2 text-[11px] text-white/55">
                                <span className="text-white/85 font-medium">{pkg.name}</span> package
                            </div>
                        )}
                    </div>
                </div>

                <nav className="px-3 flex-1 space-y-5 overflow-y-auto pb-6">
                    {NAV_GROUPS.map((g) => (
                        <div key={g.title}>
                            <div className="px-3 pb-1.5 text-[10px] uppercase tracking-widest text-white/35 font-semibold">
                                {g.title}
                            </div>
                            <div className="space-y-0.5">
                                {g.items.map((it) => {
                                    const Icon = it.icon;
                                    const locked = it.feature && !unlocked.has(it.feature);
                                    const to = resolveLink(it);
                                    return (
                                        <NavLink
                                            key={it.to}
                                            to={to}
                                            end
                                            onClick={() => setSidebarOpen(false)}
                                            className={({ isActive }) =>
                                                "ddi-nav-item group" + (isActive ? " active" : "") + (locked ? " ddi-nav-locked" : "")
                                            }
                                            data-testid={it.testid}
                                            title={locked ? `${it.label} — upgrade your package to unlock` : undefined}
                                        >
                                            <Icon size={16} strokeWidth={2.1} />
                                            <span className="flex-1 truncate">{it.label}</span>
                                            {/* W2-UIUX-NEW-005 — locked nav items get a visible "Upgrade" chip
                                                on hover (always on at md+ for clarity) plus the lock icon so
                                                starter-tier doctors see the paid feature surface + conversion CTA. */}
                                            {locked && (
                                                <>
                                                    <span
                                                        className="hidden group-hover:inline-flex items-center text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full bg-[#0F766E] text-white"
                                                        data-testid={`${it.testid}-upgrade-chip`}
                                                    >
                                                        Upgrade
                                                    </span>
                                                    <Lock size={12} className="opacity-70" />
                                                </>
                                            )}
                                        </NavLink>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="px-4 py-4 border-t border-white/8">
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#3A80F7] to-[#2456B5] flex items-center justify-center text-[13px] font-bold">
                            {initials}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold truncate">{user?.full_name}</div>
                            <div className="text-[11px] text-white/50 truncate">{user?.email}</div>
                        </div>
                        <button
                            onClick={() => { clearSession(); navigate("/login"); }}
                            className="text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10"
                            data-testid="sidebar-logout"
                            title="Log out"
                        >
                            <LogOut size={14} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main area */}
            <main className="flex-1 min-w-0 w-full">
                <header className="h-16 border-b border-[#E5E9F2]/80 bg-white/85 backdrop-blur-md flex items-center justify-between px-4 md:px-6 lg:px-8 sticky top-0 z-30 gap-3 ddi-shadow-xs">
                    <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-[#0F172A] p-1.5 -ml-1.5 rounded-md hover:bg-[#F5F7FB]" data-testid="sidebar-open" aria-label="Open menu">
                        <Menu size={20} />
                    </button>
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold hidden sm:block">
                            Doctor workspace
                        </div>
                        <div className="text-[14px] md:text-[15px] font-semibold text-[#0F172A] truncate">
                            {doctor?.full_name || user?.full_name}
                            <span className="text-[#64748B] ml-2 text-[12px] md:text-[13px] font-normal hidden sm:inline">
                                · {doctor?.specialization || ""}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 md:gap-4 shrink-0">
                        {doctor?.medical_registration_number && (
                            <div className="hidden md:flex items-center gap-1.5 text-[11px] text-[#64748B]">
                                <ShieldCheck size={12} className="text-[#16A34A]" />
                                Reg: <span className="text-[#0F172A] font-medium">{doctor.medical_registration_number}</span>
                            </div>
                        )}
                        <NotificationsBell />
                    </div>
                </header>
                <div className="p-4 md:p-6 lg:p-8 max-w-[1400px]">{children}</div>
            </main>
        </div>
    );
}

export { NAV_GROUPS };
